import { memo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  ChevronRight,
  Loader2,
  Lock,
  SearchX,
  Sparkles,
} from "lucide-react";
import { PremiumActivationDialog } from "@/components/modals/premium-activation-dialog";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { getErrorMessage } from "@/lib/api-error";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import TrendChip from "./trend-chip";
import WebsiteAnalyticsDialog from "./website-analytics-dialog";
import {
  useEnableWebsiteAnalytics,
  useMakePremiumAndEnable,
  useWebsiteAnalytics,
} from "./use-website-analytics";
import {
  formatCompactVisits,
  formatMonthShort,
  getHost,
  resolveWebsiteAnalyticsView,
} from "./utils";

/** Shared shell so states swap without the surrounding tab reflowing. */
function CardShell({
  children,
  dashed,
  tone,
}: {
  children: React.ReactNode;
  dashed?: boolean;
  tone?: "default" | "destructive";
}) {
  return (
    <div
      className={[
        "rounded-lg bg-card",
        dashed ? "border border-dashed border-border" : "border border-border",
        tone === "destructive" ? "border-destructive/30 bg-destructive/[0.03]" : "",
        dashed ? "px-4 py-6 text-center" : "px-4 py-3.5",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function IconTile({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted/40">
      {children}
    </div>
  );
}

interface WebsiteAnalyticsCardProps {
  currentDeal: DealTransformed;
}

function WebsiteAnalyticsCard({ currentDeal }: WebsiteAnalyticsCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  // Billing is org-scoped; a bare "/settings/billing" matches no route and
  // 404s. The workspace-prefixed path hits BillingRedirect, which resolves the
  // active org (and bounces to /workspaces when there is none).
  const { navigate } = useWorkspaceNavigation();
  const { quota } = useOrgQuota();
  const dealId = currentDeal.id;

  const { data, isLoading, isError, error, refetch, isRefetching } =
    useWebsiteAnalytics(dealId);
  const enableMutation = useEnableWebsiteAnalytics(dealId);
  const premiumMutation = useMakePremiumAndEnable(dealId);

  const handleMakePremium = async () => {
    await premiumMutation.mutateAsync().catch(() => undefined);
    setPremiumDialogOpen(false);
  };

  const view = resolveWebsiteAnalyticsView({
    data,
    isLoading,
    isError,
    // getErrorMessage digs out the backend's message; a raw AxiosError reads
    // "Request failed with status code 403", which tells a user nothing.
    errorMessage: isError ? getErrorMessage(error, "") || null : null,
  });

  const domain = data?.domain ?? getHost(currentDeal.overview?.website) ?? null;
  const companyName = currentDeal.overview?.name || "this company";

  switch (view.kind) {
    case "loading":
      return (
        <div className="rounded-lg border border-border bg-card px-4 py-3">
          <div className="h-2.5 w-28 animate-pulse rounded bg-muted/50" />
          <div className="mt-2 h-7 w-24 animate-pulse rounded bg-muted/50" />
          <div className="mt-2 h-2 w-40 animate-pulse rounded bg-muted/50" />
        </div>
      );

    case "disabled":
      return (
        <CardShell>
          <div className="@container flex flex-col gap-3 @sm:flex-row @sm:items-center">
            <IconTile>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">Website Analytics</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Estimated monthly visits and engagement for{" "}
                <span className="font-mono text-foreground">{domain}</span>, from
                third-party web-traffic data.
              </p>
            </div>
            <button
              type="button"
              onClick={() => enableMutation.mutate()}
              disabled={enableMutation.isPending}
              className="shrink-0 rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              {enableMutation.isPending ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Enabling…
                </span>
              ) : (
                "Enable"
              )}
            </button>
          </div>
        </CardShell>
      );

    case "needs_premium": {
      // Cap reached: the click has nowhere useful to go, so explain and route
      // to billing rather than repeating a failure.
      if (premiumMutation.capError) {
        const used =
          premiumMutation.capError.currentTracked ?? quota?.premium_deals_used;
        const limit =
          premiumMutation.capError.limit ??
          quota?.effective_premium_deals_cap ??
          quota?.premium_deals_cap;

        return (
          <CardShell tone="destructive">
            <div className="flex items-center gap-3">
              <IconTile>
                <Lock className="h-4 w-4 text-destructive" />
              </IconTile>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  Premium deal limit reached
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {used != null && limit != null
                    ? `You're using all ${limit} premium deals (${used} of ${limit}).`
                    : "You've used all of your premium deals."}{" "}
                  Upgrade your plan, or free a slot by deactivating another
                  deal.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/settings/billing")}
                className="shrink-0 rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-80"
              >
                Upgrade
              </button>
            </div>
          </CardShell>
        );
      }

      return (
        <>
          <CardShell>
            <div className="@container flex flex-col gap-3 @sm:flex-row @sm:items-center">
              <IconTile>
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </IconTile>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  Website Analytics
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Traffic estimates for{" "}
                  <span className="font-mono text-foreground">{domain}</span>{" "}
                  require this to be a premium deal.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPremiumDialogOpen(true)}
                disabled={premiumMutation.isPending}
                className="shrink-0 rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-80 disabled:opacity-50"
              >
                {premiumMutation.isPending ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Enabling…
                  </span>
                ) : (
                  "Make premium & enable"
                )}
              </button>
            </div>
          </CardShell>

          <PremiumActivationDialog
            open={premiumDialogOpen}
            onClose={() => setPremiumDialogOpen(false)}
            onConfirm={handleMakePremium}
            reportName="Website Analytics"
            nextStage={null}
            currentTracked={quota?.premium_deals_used ?? 0}
            isLoading={premiumMutation.isPending}
          />
        </>
      );
    }

    case "no_website":
      // Nothing to offer without a website, and an empty-state card on every
      // such deal is noise on a tab that is already dense. Stay silent; the
      // card appears once a website is added.
      return null;

    case "fetching":
      return (
        <CardShell>
          <div className="flex items-center gap-3">
            <IconTile>
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                Fetching traffic data…
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Usually under a minute. This card updates automatically.
              </p>
            </div>
          </div>
        </CardShell>
      );

    case "no_data":
      return (
        <CardShell dashed>
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted/40">
            <SearchX className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">
            No traffic estimates available
          </p>
          {/* The final clause is load-bearing: without it "no data" reads as
              "no traffic" and the deal gets mis-scored. */}
          <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
            Our data provider doesn&rsquo;t publish estimates for{" "}
            <span className="font-mono">{domain}</span>. This is normal for sites
            behind a login, newly registered domains, and low-traffic sites &mdash;
            it doesn&rsquo;t mean the site has no visitors.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="mt-3 rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            {isRefetching ? "Checking…" : "Check again"}
          </button>
        </CardShell>
      );

    case "error":
      return (
        <CardShell tone="destructive">
          <div className="flex items-center gap-3">
            <IconTile>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                Couldn&rsquo;t load traffic data
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {view.message ||
                  "The traffic provider didn't respond. Nothing else on this deal is affected."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isRefetching}
              className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              {isRefetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        </CardShell>
      );

    case "ok": {
      const { trend, points } = view;

      return (
        <>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            aria-haspopup="dialog"
            className="@container w-full rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-foreground/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                  Monthly Visits · Est.
                </div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">
                    {formatCompactVisits(trend.current)}
                  </span>
                  <TrendChip trend={trend} />
                </div>
                {/* Names the actual month rather than claiming "this month" -
                    the provider lags, often by one or two months. */}
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {trend.reason === "single-point" && trend.currentMonth
                    ? `${formatMonthShort(trend.currentMonth)} · only one month of data, no trend yet`
                    : trend.reason === "zero-baseline" && trend.currentMonth
                      ? `${formatMonthShort(trend.currentMonth)} · previous month reported zero visits`
                      : trend.currentMonth && trend.previousMonth
                        ? `${formatMonthShort(trend.currentMonth)} vs ${formatMonthShort(trend.previousMonth)}${
                            trend.previousIsAdjacent ? "" : " — no data in between"
                          }`
                        : "Latest available month"}
                </p>
                <p className="mt-1.5 text-[11px] text-muted-foreground/70">
                  Third-party estimate — not {companyName}&rsquo;s own analytics.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <span className="hidden @sm:inline">View details</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </button>

          {dialogOpen && (
            <WebsiteAnalyticsDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              companyName={companyName}
              domain={domain}
              data={view.data}
              trend={trend}
              points={points}
            />
          )}
        </>
      );
    }

    default:
      return null;
  }
}

export default memo(WebsiteAnalyticsCard);
